import { notification } from 'antd'
import i18next from 'i18next'
import { makeAutoObservable } from 'mobx'

import { FileEntityType, fileService } from 'services/FileService'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { WEB_LINK_REGEXP } from 'shared/constants/regExp'
import { CHECK_NETWORK_URL } from 'shared/constants/values'

import { OrderKeys } from '../../OrderModule/data'
import { eLockApi } from '../api'
import { ELockFileItem } from '../types'
import { ELockUiStore } from './ELockUiStore'

const getKey = (key: string) => `${OrderKeys.eLockModule}.api.${key}`

export class ELockApiStore {
  constructor(protected readonly uiStore: ELockUiStore) {
    makeAutoObservable(this)
  }

  async getELockConnectInfo() {
    try {
      if (this.uiStore.orderId) {
        const result = await eLockApi.getELockConnectInfo({
          orderId: this.uiStore.orderId,
          employeeId: this.uiStore.userId,
          lockName: this.uiStore.eLockName,
          lockNoSet: !this.uiStore.isActive,
        })

        if (result) {
          this.uiStore.setELockConnectInfo(result)
          return result
        } else {
          notification.error({
            message: i18next.t(getKey('getELockConnectInfoEmpty')),
          })
        }
      }
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('getELockConnectInfoError')),
        description: (error as Error).message,
      })
    }
  }

  async activateELock() {
    if (!this.uiStore.orderId) {
      return false
    }

    try {
      const savedPhotos = await this.filesUpload(this.uiStore.orderId, this.uiStore.photos)

      await eLockApi.activateELock({
        orderId: this.uiStore.orderId,
        eLockName: this.uiStore.eLockName,
        photos: savedPhotos,
      })

      notification.success({
        message: i18next.t(getKey('activateELockSuccess')),
      })

      return true
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('activateELockError')),
        description: (error as Error).message,
      })

      return false
    }
  }

  async updateELock() {
    if (!this.uiStore.orderId) {
      return
    }

    try {
      const photos = await this.filesUpload(this.uiStore.orderId, this.uiStore.photos)

      await eLockApi.updateELock({
        orderId: this.uiStore.orderId,
        eLockName: this.uiStore.eLockName,
        photos,
      })

      notification.success({
        message: i18next.t(getKey('updateELockSuccess')),
      })

      return true
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('updateELockError')),
        description: (error as Error).message,
      })
    }
  }

  async filesUpload(orderId: number, photos: ELockFileItem[]) {
    const newPhotos: ELockFileItem[] = []
    const oldPhotos: ELockFileItem[] = []

    for (const photo of photos) {
      if (WEB_LINK_REGEXP.test(photo.url)) {
        oldPhotos.push(photo)
      } else {
        newPhotos.push(photo)
      }
    }

    try {
      const res = await fileService.filesUpload({
        entityId: orderId,
        entityType: FileEntityType.pickPointOrder,
        files: newPhotos.map((photo) => ({ content: photo.url, totalSize: photo.size })),
      })

      if (res.length < newPhotos.length) {
        throw new Error(i18next.t(getKey('eLockFilesUploadError')))
      }

      return [
        ...res.map((photoItem, index) => ({
          size: photoItem.uploadedSize,
          url: photoItem.imageUrl,
          type: newPhotos[index].type,
        })),
        ...oldPhotos,
      ]
    } catch (error) {
      throw new Error(`${i18next.t(getSharedKey('api.photosUploadError'))}. ${(error as Error).message}`)
    }
  }

  async checkNetwork() {
    try {
      await fetch(CHECK_NETWORK_URL, {
        mode: 'no-cors',
        signal: AbortSignal.timeout(3000),
      })

      return true
    } catch (error) {
      return false
    }
  }
}
